import { useCallback, useEffect, useRef, useState } from 'react';
import { HOME_CATEGORY_WIRE, HOME_CATEGORY_WIRE_ALL, reconcile, STAMPS_OWNER_KEY, type CategoryStamps } from './client-sync-state';
import { HOME3_CATEGORY_SURFACES, lastVisitedKey, markVisited, readLastVisited, type Home3Category } from './last-visited';
import { useClientSyncState } from './use-client-sync-state';

/**
 * The Overview home's per-category stamps, read together and marked one at a time.
 *
 * Each tile's disc counts what arrived in ITS category since that category was last pressed, so the
 * six are independent: pressing Tasks clears the Tasks disc and leaves the rest alone. The stamps are
 * namespaced away from the hub's own surfaces (see `Home3CategorySurface`) — opening the Tasks SCREEN
 * and pressing the Tasks TILE are different events and must not clear each other's badge.
 *
 * `marked` is React state rather than a re-read, so a press clears the disc on the spot: the store is
 * written synchronously but nothing re-renders off localStorage. It starts `null` (server, and the
 * first client paint) and resolves in an effect, which is what keeps the first render from claiming a
 * count it has not read.
 *
 * The stamps are shared with the phones through the workspace's client sync state (`useClientSyncState`):
 * a press here is sent to the server, and a press on another device moves the stamp here, live. This
 * browser's own stamps stay the cache and the fallback, so the discs never wait on the network.
 */
export function useHome3CategoryVisits(): {
  stamps: Record<Home3Category, number | null> | null;
  mark: (category: Home3Category) => void;
  markAll: () => void;
} {
  const [stamps, setStamps] = useState<Record<Home3Category, number | null> | null>(null);
  const { state, markSeen, importSeen } = useClientSyncState();

  useEffect(() => {
    setStamps(
      Object.fromEntries(HOME3_CATEGORY_SURFACES.map((s) => [s.slice('home3:'.length), readLastVisited(s)])) as Record<
        Home3Category,
        number | null
      >,
    );
  }, []);

  // The server's times are another device's presses: each moves this browser's stamp forward, and the
  // result is written back so the next load starts from it even when the server is unreachable. Which
  // stamps count, and whether to upload them, is `reconcile`'s.
  const stampsRef = useRef(stamps);
  stampsRef.current = stamps;
  const uploading = useRef(false);
  const localLoaded = stamps !== null;
  useEffect(() => {
    const local = stampsRef.current;
    if (!state || !local) return;
    const result = reconcile(local, state, readOwner(), Date.now());
    writeStamps(local, result.stamps);
    if (result.owner !== undefined) writeOwner(result.owner);
    setStamps(result.stamps);
    if (!result.importSeen || uploading.current) return;
    uploading.current = true;
    void importSeen(result.importSeen).then((answer) => {
      uploading.current = false;
      if (answer?.id) writeOwner(answer.id);
    });
  }, [state, localLoaded, importSeen]);

  const mark = useCallback((category: Home3Category) => {
    const now = Date.now();
    markVisited(`home3:${category}`, now);
    setStamps((current) => (current ? { ...current, [category]: now } : current));
    void markSeen([HOME_CATEGORY_WIRE[category]]);
  }, [markSeen]);

  // Web draws no disc on "All", so nothing here changes; the phones do, and opening All on any surface clears it.
  const markAll = useCallback(() => {
    void markSeen([HOME_CATEGORY_WIRE_ALL]);
  }, [markSeen]);

  return { stamps, mark, markAll };
}

function readOwner(): string | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage.getItem(STAMPS_OWNER_KEY);
  } catch {
    return null;
  }
}

function writeOwner(owner: string | null): void {
  try {
    if (owner === null) window.localStorage.removeItem(STAMPS_OWNER_KEY);
    else window.localStorage.setItem(STAMPS_OWNER_KEY, owner);
  } catch {
    // Storage blocked: the owner is unknown again next load, so the stamps are uploaded once more.
  }
}

/** Writes the stamps that changed. A stamp that became null (another account's, dropped) is removed. */
function writeStamps(before: CategoryStamps, after: CategoryStamps): void {
  for (const category of Object.keys(after) as Home3Category[]) {
    const value = after[category];
    if (value === before[category]) continue;
    if (value !== null) {
      markVisited(`home3:${category}`, value);
      continue;
    }
    try {
      window.localStorage.removeItem(lastVisitedKey(`home3:${category}`));
    } catch {
      // Storage blocked: the stale stamp stays local to this browser and is never uploaded.
    }
  }
}
